import { Search, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { generatePath, useNavigate } from 'react-router-dom'

import { fetchMultisigGroupByAccount } from '@/api'
import { Breadcrumbs } from '@/common/Breadcrumbs'
import GradientWrapper from '@/common/GradientWrapper'
import InfiniteList, { InfiniteListLoadingStates } from '@/common/InfiniteList'
import QuestionMark from '@/common/QuestionMark'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import { useDebouncedValue } from '@/hooks'
import { RoutePaths } from '@/routes'
import { UiButton } from '@/ui/UiButton'
import UiContainer from '@/ui/UiContainer'
import { UiInput } from '@/ui/UiInput'
import { UiLabel } from '@/ui/UiLabel'
import { UiSkeleton } from '@/ui/UiSkeleton'
import { UiSwitch } from '@/ui/UiSwitch'

import GroupCard from './components/GroupCard'
import GroupCardSkeleton from './components/GroupCardSkeleton'
import { useMultisigGroupsInfiniteList } from './hooks/multisig-groups-infinite-list'

export default function Admin() {
  const { address } = useKeplrWallet()
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [showOnlyMyGroups, setShowOnlyMyGroups] = useState(false)
  const debouncedSearchQuery = useDebouncedValue(searchQuery, { delayMs: 250 })

  const updateSearchQuery = (nextValue: string) => {
    setSearchQuery(nextValue)
  }

  const multisigGroupsQuery = useMultisigGroupsInfiniteList({
    pagination: {
      limit: '20',
    },
    queryOptions: {
      staleTime: 30_000,
      refetchInterval: 30_000,
    },
  })

  const groups = useMemo(
    () => multisigGroupsQuery.data?.pages.flatMap(p => p.group) ?? [],
    [multisigGroupsQuery.data],
  )
  const normalizedSearchQuery = debouncedSearchQuery.trim().toLowerCase()

  // Smart search: if the search looks like a full group address, try to fetch it directly
  // Check if it looks like a valid address
  // (starts with the appropriate prefix and has reasonable length)
  useEffect(() => {
    const searchTrimmed = debouncedSearchQuery.trim()
    if (!searchTrimmed) return

    if (
      searchTrimmed.startsWith(
        bridgelessChainInfo.bech32Config.bech32PrefixAccAddr,
      ) &&
      searchTrimmed.length > 40
    ) {
      fetchMultisigGroupByAccount(searchTrimmed).then(group => {
        if (group?.account) {
          navigate(
            generatePath(RoutePaths.MultisigGroup, {
              account: group.account,
            }),
          )
        }
      })
    }
  }, [debouncedSearchQuery, navigate])

  const filteredGroups = useMemo(() => {
    let filtered = groups

    if (showOnlyMyGroups && address) {
      filtered = filtered.filter(group =>
        group.members.some(
          member => member.toLowerCase() === address.toLowerCase(),
        ),
      )
    }

    if (normalizedSearchQuery) {
      filtered = filtered.filter(group =>
        group.account.toLowerCase().includes(normalizedSearchQuery),
      )
    }

    return filtered
  }, [groups, normalizedSearchQuery, showOnlyMyGroups, address])

  const listLoadingState = (() => {
    if (multisigGroupsQuery.isPending && groups.length === 0) {
      return InfiniteListLoadingStates.Initial
    }

    if (multisigGroupsQuery.isError && groups.length === 0) {
      return InfiniteListLoadingStates.Error
    }

    if (multisigGroupsQuery.isFetchingNextPage) {
      return InfiniteListLoadingStates.NextLoading
    }

    if (groups.length > 0 && multisigGroupsQuery.isError) {
      return InfiniteListLoadingStates.NextError
    }

    return InfiniteListLoadingStates.Idle
  })()

  const errorTitle = (() => {
    const error = multisigGroupsQuery.error
    if (!error) return 'Failed to load multisig groups'
    if (error instanceof Error) return error.message
    return 'Failed to load multisig groups'
  })()

  if (multisigGroupsQuery.isPending && groups.length === 0) {
    return (
      <UiContainer>
        <div className='mt-26 space-y-6 px-4 md:px-0'>
          <div className='space-y-4'>
            <UiSkeleton className='h-7 w-56' />
            <UiSkeleton className='h-11.5 w-full sm:max-w-103.75' />
          </div>

          <div className='space-y-4'>
            {Array.from({ length: 6 }).map((_, index) => (
              <GroupCardSkeleton key={index} />
            ))}
          </div>
        </div>
      </UiContainer>
    )
  }

  return (
    <UiContainer>
      <div className='mt-26 space-y-6 px-4 md:px-0'>
        <div className='bg-background sticky top-0 z-10 flex flex-col gap-6 pt-2 pb-4'>
          <Breadcrumbs
            items={[
              { label: 'Home', to: RoutePaths.Root },
              { label: 'Groups' },
            ]}
          />
          <div className='space-y-4'>
            <div className='flex flex-wrap items-start justify-between gap-4'>
              <h1 className='text-foreground text-xl font-semibold md:text-2xl'>
                Multisig Groups
              </h1>
              {address && (
                <div className='flex items-center gap-2'>
                  <UiSwitch
                    id='show-my-groups'
                    checked={showOnlyMyGroups}
                    onCheckedChange={setShowOnlyMyGroups}
                  />
                  <UiLabel
                    htmlFor='show-my-groups'
                    className='cursor-pointer text-sm'
                  >
                    Show only my groups
                  </UiLabel>
                </div>
              )}
            </div>
            <div className='relative flex-1 sm:max-w-103.75'>
              <GradientWrapper>
                <UiInput
                  value={searchQuery}
                  placeholder='Search by group address'
                  className='h-11.5 px-11'
                  onChange={event => updateSearchQuery(event.target.value)}
                />
              </GradientWrapper>

              <Search className='text-primary pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2' />

              {searchQuery.trim().length > 0 && (
                <UiButton
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='absolute top-1/2 right-1 h-8 w-8 -translate-y-1/2'
                  aria-label='Clear search'
                  onClick={() => {
                    updateSearchQuery('')
                  }}
                >
                  <X />
                </UiButton>
              )}
            </div>
          </div>
        </div>

        <InfiniteList
          items={groups}
          loadingState={listLoadingState}
          hasNextPage={multisigGroupsQuery.hasNextPage}
          errorTitle={errorTitle}
          noDataTitle='Groups not found'
          onRetry={() => multisigGroupsQuery.refetch()}
          onLoadNext={() => multisigGroupsQuery.fetchNextPage()}
        >
          <div className='space-y-4'>
            {(() => {
              if (filteredGroups.length > 0) {
                return filteredGroups.map(group => (
                  <GroupCard key={group.account} group={group} />
                ))
              }

              if (!normalizedSearchQuery) return null

              return (
                <div className='text-foreground flex w-full flex-col items-center gap-2.5 rounded-lg p-6 text-center'>
                  <div className='text-primary bg-primary-foreground aspect-square w-25 rounded-xl p-6'>
                    <QuestionMark />
                  </div>
                  <p className='mt-5 text-xl'>No matching groups found</p>
                  <p className='text-muted-foreground mt-2 text-xs'>
                    Try a different address or keep scrolling to load more.
                  </p>
                </div>
              )
            })()}
          </div>
        </InfiniteList>
      </div>
    </UiContainer>
  )
}
